import { useState, useEffect, useRef, useMemo } from "react"
import styled from "styled-components"
import { Search, SlidersHorizontal, X } from 'lucide-react'
import Calendar from 'react-calendar'
import colors from "theme/colors"
import breakpoints from "theme/breakpoints"
import Select from 'react-select';
import date from "helpers/date"
import promotionalCodesApi from "api/requests/promotionalCodes"
import { useEvent } from "context/events/Event"
import { useUser } from "context/user/User"
import { isEventCollaboratorOnly } from "helpers/dashboardAccess"
import ticketsApi from "api/requests/tickets"
import debounce from 'helpers/debounce';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { formatDay } from './participantTokens';

/** Atalhos para o estado de check-in — o filtro mais usado na portaria. */
const QUICK_STATUS = [
    { id: '', label: 'Todos' },
    { id: '0', label: 'Não entraram' },
    { id: '1', label: 'Já entraram' },
];

const PRODUCT_TYPES = [
    { label: 'Todos', value: '' },
    { label: 'Ingresso', value: 'ticket' },
    { label: 'Produto', value: 'product' },
];

const PRODUCT_STATUS = [
    { label: 'Todos', value: '' },
    { label: 'Não utilizado', value: '0' },
    { label: 'Utilizado', value: '1' },
];

const EMPTY_FILTERS = {
    search: '',
    type: '',
    status: '',
    day: '',
    dateRange: [],
    coupons: new Set(),
    tickets: new Set(),
};

export default function ParticipantsFilters({ onApplyFilter, onFiltersChange, checkin }) {
    const { event } = useEvent();
    const { user } = useUser();
    // Os cupons são do produtor: quem foi convidado só para o evento não os lista.
    const canFilterByCoupon = !isEventCollaboratorOnly(user);

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [showFilters, setShowFilters] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [couponsLoading, setCouponsLoading] = useState(false);
    const [couponOptions, setCouponOptions] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [itemOptions, setItemOptions] = useState([]);

    const popoverRef = useRef(null);

    const setToCommaString = (value) =>
        value && value.size > 0 ? [...value].join(', ') : null;

    const safeDate = (value) =>
        value instanceof Date && !isNaN(value) ? value.toISOString().split('T')[0] : null;

    const removeFalsyValues = (obj) =>
        Object.fromEntries(
            Object.entries(obj).filter(
                ([, v]) => v !== null && v !== undefined && v !== ''
            )
        );

    const selectedValues = useMemo(() => {
        return removeFalsyValues({
            type: filters.type || '',
            used: filters.status || '',
            day: filters.day || '',
            coupons: setToCommaString(filters.coupons),
            tickets: setToCommaString(filters.tickets),
            start_date: safeDate(filters.dateRange?.[0]),
            end_date: safeDate(filters.dateRange?.[1]),
            term: filters.search?.trim() || null,
        });
    }, [filters]);

    /* `coupons` e `tickets` são sempre Set — antes o estado inicial e o
       clearFilters usavam array, então `.size` ficava undefined e o contador de
       filtros ativos nunca somava esses dois. */
    const activeChips = useMemo(() => {
        const chips = [];

        if (filters.type) {
            chips.push({
                key: 'type',
                label: PRODUCT_TYPES.find((o) => o.value === filters.type)?.label,
                clear: () => setFilters((p) => ({ ...p, type: '', tickets: new Set() })),
            });
        }

        if (filters.status) {
            chips.push({
                key: 'status',
                label: PRODUCT_STATUS.find((o) => o.value === filters.status)?.label,
                clear: () => setFilters((p) => ({ ...p, status: '' })),
            });
        }

        if (filters.dateRange?.length === 2) {
            chips.push({
                key: 'date',
                label: `${date.format(filters.dateRange[0])} → ${date.format(filters.dateRange[1])}`,
                clear: () => setFilters((p) => ({ ...p, dateRange: [] })),
            });
        }

        filters.coupons.forEach((code) => {
            chips.push({
                key: `coupon-${code}`,
                label: `Cupom ${code}`,
                clear: () =>
                    setFilters((p) => {
                        const next = new Set(p.coupons);
                        next.delete(code);
                        return { ...p, coupons: next };
                    }),
            });
        });

        filters.tickets.forEach((id) => {
            chips.push({
                key: `item-${id}`,
                label: itemOptions.find((o) => o.value === id)?.label || 'Item',
                clear: () =>
                    setFilters((p) => {
                        const next = new Set(p.tickets);
                        next.delete(id);
                        return { ...p, tickets: next };
                    }),
            });
        });

        return chips;
    }, [filters, itemOptions]);

    const hasActiveFilters = activeChips.length > 0 || filters.search.length > 0;

    /* Só no check-in por dia. Sem escolha vale o dia que a API devolveu (hoje, ou
       "todos os dias" quando hoje não é dia do evento). */
    const perDay = checkin?.mode === 'per_day' && checkin.days?.length > 0;
    const selectedDay = filters.day || checkin?.day;

    const handleTermChange = debounce((searchTerm) => {
        setFilters((prev) => ({ ...prev, search: searchTerm }));
    }, 500);

    function clearFilters() {
        setFilters(EMPTY_FILTERS);
    }

    async function getCoupons() {
        setCouponsLoading(true);

        try {
            const { data } = await promotionalCodesApi.getByEvent(event?.slug);
            setCouponOptions(data.map((coupon) => ({ value: coupon.code, label: coupon.code })));
        } catch (error) {
            console.error("Erro ao buscar cupons:", error);
        } finally {
            setCouponsLoading(false);
        }
    }

    async function getItems(type) {
        setItemsLoading(true);

        try {
            const { data } = await ticketsApi.getAllItems(event?.slug, { type, deleted: 'include' });
            setItemOptions(data.map((item) => ({ value: item.id, label: item.name })));
        } catch (error) {
            console.error("Erro ao buscar itens:", error);
        } finally {
            setItemsLoading(false);
        }
    }

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setShowCalendar(false);
            }
        };

        if (showCalendar) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCalendar]);

    useEffect(() => {
        onApplyFilter(selectedValues);
        onFiltersChange?.(selectedValues);
    }, [selectedValues]);

    useEffect(() => {
        if (filters.type) getItems(filters.type);
    }, [filters.type]);

    useEffect(() => {
        if (canFilterByCoupon) getCoupons();
    }, []);

    return (
        <Container>
            <TopRow>
                <SearchWrap>
                    <SearchIcon aria-hidden='true'>
                        <Search size={16} strokeWidth={2.2} />
                    </SearchIcon>
                    <SearchInput
                        type='search'
                        placeholder='Buscar por nome, e-mail ou código...'
                        aria-label='Buscar participante por nome, e-mail ou código'
                        onChange={(e) => handleTermChange(e.target.value)}
                    />
                </SearchWrap>

                <AdvancedToggle
                    type='button'
                    $open={showFilters}
                    aria-expanded={showFilters}
                    onClick={() => setShowFilters((prev) => !prev)}
                >
                    <SlidersHorizontal size={15} strokeWidth={2.2} aria-hidden='true' />
                    Filtros
                    {activeChips.length > 0 && <Badge>{activeChips.length}</Badge>}
                </AdvancedToggle>
            </TopRow>

            {/* Atalho de estado de check-in: o filtro que a portaria mais usa
                sai de dentro do painel avançado e vira segmented control. */}
            <QuickRow role='group' aria-label='Filtrar por estado de check-in'>
                {QUICK_STATUS.map((option) => (
                    <QuickButton
                        key={option.id || 'all'}
                        type='button'
                        $active={filters.status === option.id}
                        aria-pressed={filters.status === option.id}
                        onClick={() => setFilters((prev) => ({ ...prev, status: option.id }))}
                    >
                        {option.label}
                    </QuickButton>
                ))}
            </QuickRow>

            {perDay && (
                <DayRow role='group' aria-label='Dia do check-in'>
                    <DayLabel>Dia do check-in</DayLabel>
                    <QuickRow>
                        {checkin.days.map((day) => (
                            <QuickButton
                                key={day}
                                type='button'
                                $active={selectedDay === day}
                                aria-pressed={selectedDay === day}
                                onClick={() => setFilters((prev) => ({ ...prev, day }))}
                            >
                                {formatDay(day)}{day === checkin.today ? ' · hoje' : ''}
                            </QuickButton>
                        ))}
                        <QuickButton
                            type='button'
                            $active={selectedDay === 'all'}
                            aria-pressed={selectedDay === 'all'}
                            onClick={() => setFilters((prev) => ({ ...prev, day: 'all' }))}
                        >
                            Todos os dias
                        </QuickButton>
                    </QuickRow>
                </DayRow>
            )}

            {showFilters && (
                <ExpandedFilters>
                    <FilterGroup>
                        <FilterLabel>Tipo</FilterLabel>
                        <Select
                            placeholder='Selecione'
                            options={PRODUCT_TYPES}
                            noOptionsMessage={() => 'Nenhuma opção disponível'}
                            value={PRODUCT_TYPES.find((o) => o.value === filters.type) || PRODUCT_TYPES[0]}
                            onChange={(selected) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    type: selected ? selected.value : '',
                                    tickets: new Set(),
                                }))
                            }
                        />
                    </FilterGroup>

                    {filters.type && (
                        <FilterGroup>
                            <FilterLabel>
                                {filters.type === 'ticket' ? 'Ingresso' : 'Produto'}
                            </FilterLabel>
                            <Select
                                isMulti
                                placeholder={itemsLoading ? 'Carregando...' : 'Selecione'}
                                options={itemOptions}
                                noOptionsMessage={() =>
                                    itemsLoading
                                        ? 'Carregando...'
                                        : `Nenhum ${filters.type === 'ticket' ? 'ingresso' : 'produto'} disponível`
                                }
                                value={[...filters.tickets]
                                    .map((id) => itemOptions.find((o) => o.value === id))
                                    .filter(Boolean)}
                                onChange={(selected) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        tickets: new Set(selected.map((o) => o.value)),
                                    }))
                                }
                            />
                        </FilterGroup>
                    )}

                    {/* Antes este Select recebia `productTypes.find(...)`, a lista
                        errada — nunca encontrava a opção e caía em modo não
                        controlado. */}
                    <FilterGroup>
                        <FilterLabel>Status</FilterLabel>
                        <Select
                            placeholder='Selecione'
                            options={PRODUCT_STATUS}
                            noOptionsMessage={() => 'Nenhuma opção disponível'}
                            value={PRODUCT_STATUS.find((o) => o.value === filters.status) || PRODUCT_STATUS[0]}
                            onChange={(selected) =>
                                setFilters((prev) => ({ ...prev, status: selected ? selected.value : '' }))
                            }
                        />
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>Data da compra</FilterLabel>
                        <CalendarAnchor>
                            <CalendarTrigger
                                type='button'
                                onClick={() => setShowCalendar((prev) => !prev)}
                                aria-expanded={showCalendar}
                            >
                                {filters.dateRange?.length === 2
                                    ? `${date.format(filters.dateRange[0])} até ${date.format(filters.dateRange[1])}`
                                    : 'Selecione o intervalo'}
                            </CalendarTrigger>

                            {showCalendar && (
                                <CalendarPopover ref={popoverRef} onClick={(e) => e.stopPropagation()}>
                                    <Calendar
                                        selectRange
                                        value={filters.dateRange}
                                        onChange={(d) => setFilters((prev) => ({ ...prev, dateRange: d }))}
                                    />
                                    <CalendarFooter>
                                        <GhostButton
                                            type='button'
                                            onClick={() => setFilters((prev) => ({ ...prev, dateRange: [] }))}
                                        >
                                            Limpar data
                                        </GhostButton>
                                    </CalendarFooter>
                                </CalendarPopover>
                            )}
                        </CalendarAnchor>
                    </FilterGroup>

                    {canFilterByCoupon && <FilterGroup>
                        <FilterLabel>Cupons</FilterLabel>
                        <Select
                            isMulti
                            placeholder={couponsLoading ? 'Carregando...' : 'Selecione'}
                            options={couponOptions}
                            noOptionsMessage={() =>
                                couponsLoading ? 'Carregando...' : 'Nenhum cupom disponível'
                            }
                            value={[...filters.coupons].map((value) => ({ value, label: value }))}
                            onChange={(selected) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    coupons: new Set(selected.map((o) => o.value)),
                                }))
                            }
                        />
                    </FilterGroup>}
                </ExpandedFilters>
            )}

            {/* Chips removíveis: antes o único sinal de filtro ativo era um
                número no badge, sem dizer o que estava filtrado. */}
            {hasActiveFilters && (
                <ChipsRow>
                    {activeChips.map((chip) => (
                        <Chip key={chip.key}>
                            {chip.label}
                            <ChipClear
                                type='button'
                                onClick={chip.clear}
                                aria-label={`Remover filtro ${chip.label}`}
                            >
                                <X size={12} strokeWidth={2.6} aria-hidden='true' />
                            </ChipClear>
                        </Chip>
                    ))}

                    <ClearAll type='button' onClick={clearFilters}>
                        Limpar tudo
                    </ClearAll>
                </ChipsRow>
            )}
        </Container>
    );
}

/* ─────────────────────────────  Estilos  ───────────────────────────── */

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TopRow = styled.div`
    display: flex;
    gap: 10px;
    align-items: stretch;
`;

const SearchWrap = styled.div`
    position: relative;
    flex: 1;
    min-width: 0;
`;

const SearchIcon = styled.span`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    color: ${colors.night};
    pointer-events: none;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 42px;
    padding: 0 14px 0 36px;
    border-radius: 10px;
    border: 1px solid rgba(16, 24, 40, 0.14);
    background: ${colors.neutralLight};
    font-size: 14px;
    color: ${colors.nightDark};

    &::placeholder {
        color: ${colors.night};
    }

    &:focus {
        outline: none;
        border-color: ${panelAccent(colors.secondaryPurple)};
        box-shadow: 0 0 0 3px ${panelAccentAlpha(colors.secondaryPurple, '22')};
    }
`;

const AdvancedToggle = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 42px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid ${({ $open }) => ($open ? panelAccent(colors.secondaryPurple) : 'rgba(16,24,40,0.14)')};
    background: ${({ $open }) => ($open ? '#F7EFFF' : colors.neutralLight)};
    color: ${({ $open }) => ($open ? '#6D14CC' : colors.nightDark)};
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: ${panelAccent(colors.secondaryPurple)};
    color: ${colors.neutralLight};
    font-size: 10px;
    font-weight: 600;
`;

const QuickRow = styled.div`
    display: inline-flex;
    align-self: flex-start;
    max-width: 100%;
    padding: 3px;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const DayRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
`;

const DayLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.night};
`;

const QuickButton = styled.button`
    padding: 7px 14px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    white-space: nowrap;
    cursor: pointer;
    background: ${({ $active }) => ($active ? colors.neutralLight : 'transparent')};
    color: ${({ $active }) => ($active ? colors.nightDark : colors.night)};
    box-shadow: ${({ $active }) => ($active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none')};
    transition: background 0.15s ease, color 0.15s ease;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const ExpandedFilters = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid rgba(16, 24, 40, 0.1);
    background: #fbfbfd;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr 1fr;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const FilterLabel = styled.label`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const CalendarAnchor = styled.div`
    position: relative;
    width: 100%;
`;

const CalendarTrigger = styled.button`
    width: 100%;
    height: 38px;
    padding: 0 12px;
    text-align: left;
    border-radius: 4px;
    border: 1px solid hsl(0, 0%, 80%);
    background: ${colors.neutralLight};
    font-size: 14px;
    color: ${colors.nightDark};
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const CalendarPopover = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 99;
    padding: 1rem;
    border-radius: 10px;
    background: ${colors.neutralLight};
    box-shadow: 0 10px 30px rgba(16, 24, 40, 0.16);

    .react-calendar {
        border: none;
    }
`;

const CalendarFooter = styled.div`
    margin-top: 0.75rem;
    display: flex;
    justify-content: flex-end;
`;

const GhostButton = styled.button`
    padding: 7px 12px;
    border-radius: 8px;
    border: 1px solid rgba(16, 24, 40, 0.12);
    background: transparent;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    cursor: pointer;

    &:hover {
        background: ${colors.neutralBackground};
    }
`;

const ChipsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 26px;
    padding: 0 6px 0 10px;
    border-radius: 999px;
    background: #f7efff;
    border: 1px solid #dfc8f8;
    color: #6d14cc;
    font-size: 12px;
    font-weight: 600;
`;

const ChipClear = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover {
        background: #e6d3fa;
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 1px;
    }
`;

const ClearAll = styled.button`
    padding: 0 8px;
    height: 26px;
    border: none;
    background: transparent;
    color: ${colors.night};
    font-size: 12px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: ${colors.nightDark};
    }
`;
